import type Dagre from '@dagrejs/dagre';
import type { ERDFlowOut } from '@repo/api-codegen';
import type { QueryClient } from '@tanstack/react-query';
import type {
	Connection,
	EdgeChange,
	FinalConnectionState,
	NodeChange,
	OnConnectStartParams,
	ReactFlowInstance,
} from '@xyflow/react';
import { applyEdgeChanges, applyNodeChanges } from '@xyflow/react';
import { makeAutoObservable, runInAction } from 'mobx';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { ERDFlowEdge } from '../components/TableEdge/TableEdge.types';
import type { ERDFlowNode } from '../components/TableNode/TableNode.types';
import { calculateLayout } from './ERDFlowStore.utils';

class ERDFlowStore {
	// REACT QUERY CLIENT
	queryClient: QueryClient;

	// CORE PROPERTIES FOR REACTFLOW, LAYOUT AND POSITIONING
	reactFlowInstance: ReactFlowInstance<ERDFlowNode, ERDFlowEdge> | undefined;

	graph: Dagre.graphlib.Graph | undefined;

	// NODE PROPERTIES
	rootId: string;
	nodes: ERDFlowNode[] = [];

	// EDGE PROPERTIES
	tableEdges: ERDFlowEdge[] = [];
	columnEdges: ERDFlowEdge[] = [];

	activeColumnId: string | undefined;
	highlightedColumnIds: Set<string> = new Set();
	highlightedColumnEdgeIds: Set<string> = new Set();

	// UI PROPERTIES
	isFullscreen: boolean = false;
	isLoading: boolean = false;

	constructor(rootId: string, queryClient: QueryClient) {
		this.rootId = rootId;
		this.queryClient = queryClient;

		makeAutoObservable(this);
	}

	get edges(): ERDFlowEdge[] {
		const columnEdges =
			this.highlightedColumnEdgeIds.size > 0
				? this.columnEdges
						.filter((edge) => this.highlightedColumnEdgeIds.has(edge.id))
						.map((edge) => ({
							...edge,
							data: {
								is_key_relationship: edge.data?.is_key_relationship ?? false,
								is_join_relationship: edge.data?.is_join_relationship ?? false,
								is_highlighted: true,
							},
						}))
				: this.columnEdges;

		return [...this.tableEdges, ...columnEdges];
	}

	// ACTION HANDLER FOR INITIALIZATION
	onInitReactFlow = async (
		reactFlowInstance: ReactFlowInstance<ERDFlowNode, ERDFlowEdge>
	) => {
		this.reactFlowInstance = reactFlowInstance;
	};

	onInitData = ({ data }: { data: ERDFlowOut }) => {
		const { updatedGraph, updatedNodes } = calculateLayout({
			nodes: data.nodes,
			edges: data.edges as ERDFlowEdge[],
		});

		runInAction(() => {
			this.graph = updatedGraph;
			this.nodes = updatedNodes;
			const tableEdges: ERDFlowEdge[] = [];
			const columnEdges: ERDFlowEdge[] = [];

			data.edges.forEach((edge) => {
				if (edge.type === 'TABLE') {
					tableEdges.push(edge as ERDFlowEdge);
				} else {
					columnEdges.push(edge as ERDFlowEdge);
				}
			});

			this.tableEdges = tableEdges;
			this.columnEdges = columnEdges;
		});
	};

	// ACTION HANDLER TO SET ACTIVE COLUMN ID
	onSetActiveColumnId = (activeColumnId: string | undefined) => {
		runInAction(() => {
			const isTogglingOff = this.activeColumnId === activeColumnId;

			this.activeColumnId = isTogglingOff ? undefined : activeColumnId;

			if (isTogglingOff) {
				this.highlightedColumnIds = new Set();
				this.highlightedColumnEdgeIds = new Set();
			} else {
				const matchingEdges = this.columnEdges.filter((edge) => {
					const sourceId = edge.sourceHandle?.split('COLUMN_SOURCE--')[1];
					const targetId = edge.targetHandle?.split('COLUMN_TARGET--')[1];
					return sourceId === activeColumnId || targetId === activeColumnId;
				});

				this.highlightedColumnEdgeIds = new Set(
					matchingEdges.map((edge) => edge.id)
				);

				this.highlightedColumnIds = new Set(
					matchingEdges.flatMap((edge) => {
						const sourceId = edge.sourceHandle?.split('COLUMN_SOURCE--')[1];
						const targetId = edge.targetHandle?.split('COLUMN_TARGET--')[1];
						return [sourceId, targetId].filter(Boolean) as string[];
					})
				);
			}
		});
	};

	// ACTION HANDLER TO NAVIGATE TO NODE
	onNavigateToNode = ({
		nodeID,
		zoom = 1,
		duration = 1000,
	}: {
		nodeID: string;
		zoom?: number;
		duration?: number;
	}) => {
		if (!this.reactFlowInstance) return;

		const node = this.reactFlowInstance.getNode(nodeID);
		if (!node) return;

		// this.reactFlowInstance.setCenter(
		// 	node.position.x + ENTITY_NODE_WIDTH / 2,
		// 	node.position.y +
		// 		(node.measured?.height ?? calculateEntityNodeHeight(node)) / 2,
		// 	{ zoom, duration }
		// );
	};

	// ACTION HANDLER TO INTERACT WITH FULLSCREEN
	onEnterFullscreen = () => {
		this.isFullscreen = true;
		this.onNavigateToNode({
			nodeID: this.rootId,
			zoom: 0.5,
			duration: 0,
		});
	};

	onExitFullscreen = () => {
		this.isFullscreen = false;
	};

	// REACTFLOW ACTION HANDLERS
	onNodesChange = (changes: NodeChange<ERDFlowNode>[]) => {
		this.nodes = applyNodeChanges(changes, this.nodes);
	};

	onEdgesChange = (changes: EdgeChange<ERDFlowEdge>[]) => {
		this.tableEdges = applyEdgeChanges(changes, this.tableEdges);
		this.columnEdges = applyEdgeChanges(changes, this.columnEdges);
	};

	onNodeClick = (event: ReactMouseEvent, node: ERDFlowNode) => {};

	onEdgeClick = (event: ReactMouseEvent, edge: ERDFlowEdge) => {};

	onEdgeMouseEnter = (event: ReactMouseEvent, edge: ERDFlowEdge) => {};

	onEdgeMouseLeave = (event: ReactMouseEvent, edge: ERDFlowEdge) => {};

	onConnect = (connection: Connection) => {};

	onConnectStart = (
		event: MouseEvent | TouchEvent,
		params: OnConnectStartParams
	) => {};

	onConnectEnd = (
		event: MouseEvent | TouchEvent,
		connectionState: FinalConnectionState
	) => {};
}

export default ERDFlowStore;
